import React, { useEffect, useRef } from 'react';
import { Network, CircuitBoard, Microscope, Brain, HeartPulse, Activity } from 'lucide-react';
import { researchAreas } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

const iconMap = {
  Network,
  CircuitBoard,
  Microscope,
  Brain,
  HeartPulse,
  Activity
};

const Research = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal').forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('active');
              }, index * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="research" className="py-28 bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 grid-overlay opacity-10"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-24 reveal">
          <h2 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '-0.02em' }}>
            Research <span className="gradient-text">Areas</span>
          </h2>
          <div className="w-40 h-2 bg-gradient-to-r from-indigo-500 via-cyan-500 to-amber-500 mx-auto mb-10 rounded-full shadow-lg"></div>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto font-light leading-relaxed">
            Exploring the frontiers of systems biology, synthetic circuits, and translational medicine with cutting-edge technologies
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {researchAreas.map((area, index) => {
            const Icon = iconMap[area.icon];
            return (
              <Card 
                key={area.id}
                className="reveal group glass-dark hover:glass-light transition-all duration-500 hover-lift bg-slate-900/40 border-slate-700/30 hover:border-indigo-400/50 overflow-hidden"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div 
                  className="h-48 bg-cover bg-center relative overflow-hidden"
                  style={{ backgroundImage: `url(${area.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-900/80 to-transparent group-hover:from-indigo-950/95 transition-all duration-300"></div>
                  <div className="absolute inset-0 bg-indigo-600/0 group-hover:bg-indigo-600/10 transition-all duration-300"></div>
                  
                  <div className="absolute bottom-4 left-4 z-10">
                    <div className="premium-gradient p-3 rounded-xl shadow-2xl group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 border border-white/10">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                  </div>
                  
                  {/* Animated corner accent */}
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-cyan-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                <CardHeader className="pb-3 pt-5">
                  <CardTitle className="text-xl md:text-2xl text-white group-hover:text-cyan-400 transition-colors duration-300" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {area.title}
                  </CardTitle>
                </CardHeader>
                
                <CardContent>
                  <CardDescription className="text-slate-300 text-sm md:text-base leading-relaxed group-hover:text-slate-200 transition-colors font-light">
                    {area.description}
                  </CardDescription>
                </CardContent>
                
                {/* Bottom accent line */}
                <div className="h-1 premium-gradient opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Research;
